import React, { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { CategoryDTO, PackageDTO, PackageImageUrlDTO, fetchCategories, updatePackage, addPackage } from "../CategoryService";
import { useNavigate } from "react-router-dom";

interface PackageFormProps {
  categories: CategoryDTO[];
  setCategories: (categories: CategoryDTO[]) => void;
  onPackageAdded: (newPackage: PackageDTO) => void;
  initialData?: PackageDTO;
  initialImageUrls: PackageImageUrlDTO[];
  isEditing: boolean;
  handleCancel: () => void;
  // Return false to stop the save (e.g. unsaved pricing/date edits the user wants to keep)
  beforeSubmit?: () => boolean;
}

const PackageForm: React.FC<PackageFormProps> = ({
  categories,
  setCategories,
  onPackageAdded,
  initialData,
  initialImageUrls,
  isEditing,
  handleCancel,
  beforeSubmit,
}) => {
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [categoryId, setCategoryId] = useState<number>(0);
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [categoryName, setCategoryName] = useState<string>("");
  const [priceFrom, setPriceFrom] = useState<number>(0);
  const [maxGroupSize, setMaxGroupSize] = useState<number>(0);
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [images, setImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<PackageImageUrlDTO[]>(initialImageUrls);
  const [mainImageIndex, setMainImageIndex] = useState<number | undefined>(undefined);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [packageUrl, setPackageUrl] = useState<string>("");

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("accessToken");

  const navigate = useNavigate();

  useEffect(() => {
    const loadCategories = async () => {
      if (apiUrl && token) {
        const categories = await fetchCategories(apiUrl);
        setCategories(categories);
      }
    };

    loadCategories();
  }, [apiUrl, setCategories]);

  useEffect(() => {
  if (initialData) {
    setName(initialData.name);
    setDescription(initialData.description);
    setCategoryId(initialData.categoryId);
    setCategoryName(initialData.categoryName || "");
    setPriceFrom(initialData.priceFrom);
    setMaxGroupSize(initialData.maxGroupSize);
    setImageUrls(initialImageUrls);
    setPackageUrl(initialData.packageUrl || "");
    const mainImageIndex = initialImageUrls.findIndex(img => img.isMain);
    setMainImageIndex(mainImageIndex !== -1 ? mainImageIndex : undefined);
    console.log("Main image index useEffect:", mainImageIndex);
    console.log("Initial image URLs:", initialImageUrls);

    // Fix: Only flatMap if prices is an array
    const pricesArray = Array.isArray(initialData.prices) ? initialData.prices : [];
    const dateRangeSet = new Set<string>();
    const uniqueDateRanges = pricesArray.flatMap(price => price.dateRanges || [])
      .filter(dateRange => {
        const dateRangeKey = `${dateRange.startDate}-${dateRange.endDate}`;
        if (dateRangeSet.has(dateRangeKey)) {
          return false;
        } else {
          dateRangeSet.add(dateRangeKey);
          return true;
        }
      }).map(dateRange => ({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }));

    console.log('uniqueDateRanges:', uniqueDateRanges);

  } else {
    // Reset form fields when initialData is null
    setName("");
    setDescription("");
    setCategoryId(0);
    setCategoryName("");
    setPriceFrom(0);
    setMaxGroupSize(0);
    setImages([]);
    setMainImageIndex(undefined);
    setPackageUrl("");
  }
}, [initialData, initialImageUrls]);
  const handleRemoveImageUrl = async (imageUrl: string, packageId: number) => {
    try {
      console.log("Deleting image:", imageUrl);
      const requestUrl = `${apiUrl}/Package/deletepackageImage/${packageId}`;
      const response = await axios.delete(requestUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params: { imageUrl },
      });

      const newImageUrls = imageUrls.filter((img) => img.url !== imageUrl);
      setImageUrls(newImageUrls);
    } catch (error: any) {
      console.error("Error deleting image", error);
    }
  };

  const handleFileUpload = (files: File[]) => {
    const newImageUrls = [...imageUrls];
    const newSelectedFiles = [...selectedFiles];

    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newImageUrls.push({
          id: 0, // Set id to 0 for new images
          url: reader.result as string,
          isMain: false,
        });
        newSelectedFiles.push(file);
        setImageUrls([...newImageUrls]);
        setSelectedFiles([...newSelectedFiles]);
      };
      reader.readAsDataURL(file);
    });
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFileUpload(acceptedFiles);
    },
    [imageUrls, selectedFiles]
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop });
 // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const logFormData = (formData: FormData) => {
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (beforeSubmit && !beforeSubmit()) {
      return;
    }

    let isValid = true;
    if (!name) {
        alert("Name cannot be empty.");
        isValid = false;
    }
    if (!description) {
        alert("Description cannot be empty.");
        isValid = false;
    }
    if (priceFrom < 0) {
        alert("Price From must be a positive number.");
        isValid = false;
    }
    if (maxGroupSize < 1) {
        alert("Max Group Size must be at least 1.");
        isValid = false;
    }
    if (!packageUrl) {
        alert("Package URL cannot be empty.");
        isValid = false;
    }

    if (!isValid) {
        return;
    }

    try {
        const selectedCategory = categories.find(category => category.id === categoryId);
        if (!selectedCategory) {
            alert("Selected category not found.");
            return;
        }

        // Update isMain property based on mainImageIndex
        const updatedImageUrls = imageUrls.map((img, index) => ({
          ...img,
          isMain: index === mainImageIndex
        }));

        const mainImageId = updatedImageUrls.find(img => img.isMain)?.id;

        const packageDto: PackageDTO = {
            id: initialData?.id || 0,
            name,
            description,
            categoryId,
            categoryName: selectedCategory.name, // Use the name from the selected category
            priceFrom,
            maxGroupSize,
            // Prices and dates are saved only by the pricing form; sending a stale copy here would delete newer ones
            prices: [],
            imageUrls: updatedImageUrls, // Ensure imageUrls are included
            packageUrl,
            mainImageId: mainImageId || undefined
        };

        const formData = new FormData();
        formData.append('packageDto', JSON.stringify(packageDto)); // Ensure JSON serialization
        formData.append('prices', JSON.stringify(packageDto.prices)); // Ensure prices are serialized
        console.log("Form data:", formData);
        selectedFiles.forEach((file) => {
            formData.append('images', file);
        });

        if (mainImageIndex !== undefined) {
            formData.append('mainImageIndex', mainImageIndex.toString());
            if (mainImageId !== undefined) {
                formData.append('mainImageId', mainImageId.toString());
            }
            console.log("Main image index:", mainImageIndex);
            console.log("Main image ID:", mainImageId);
        }

        // Log FormData entries // DEBUG ONLY
       // logFormData(formData);

        let response;
        if (isEditing) {
            response = await updatePackage(packageDto, selectedFiles, mainImageIndex, apiUrl, mainImageId);
        } else {
            response = await addPackage(packageDto, selectedFiles, mainImageIndex, apiUrl);
        }

        if (response.success) {
            onPackageAdded(response.data);
            navigate("/newPackageManager"); // Navigate back to the package list
        } else {
            alert(`Failed to ${isEditing ? "update" : "add"} package: ${response.message}`);
        }
    } catch (error: any) {
        console.error("Error submitting package", error);
        if (error.response) {
            console.error("Response data:", error.response.data);
            console.error("Response status:", error.response.status);
            console.error("Response headers:", error.response.headers);
        }
    }
  };

  return (
    <section className="container packageForm-section">
      <form onSubmit={handleSubmit} className="packageForm">
        <div className="form-group">
          <label className="form-label">
            Name:
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
        </div>
        <div className="form-group">
          <label className="form-label">
            Description:
            <textarea
              className="form-control"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
        </div>
        <div className="form-group">
  <label className="form-label">
    Category:
    <div className="custom-select-wrapper">
      <select
        className="form-control custom-select"
        value={categoryId}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setCategoryId(Number(e.target.value))
        }
      >
        <option value={undefined}>Select a category</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>
    </div>
  </label>
</div>
        <div className="d-flex justify-content-between">
          <div className="form-group">
            <label className="form-label">
              Price From:
              <input
                className="form-control"
                type="number"
                value={priceFrom}
                onChange={(e) => setPriceFrom(parseInt(e.target.value))}
              />
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">
              Max Group Size:
              <input
                className="form-control"
                type="number"
                value={maxGroupSize}
                onChange={(e) => setMaxGroupSize(parseInt(e.target.value))}
              />
            </label>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">
            Package URL:
            <input
              type="text"
              className="form-control"
              value={packageUrl}
              onChange={(e) => setPackageUrl(e.target.value)}
            />
          </label>
        </div>
        <div className="form-group">
          <label className="form-label">Images</label>
          {imageUrls.map((img: any, index: any) => (
            <div key={index} className="input-group">
              <img
                src={img.url} // Use the original URL without query parameter
                alt={`Preview ${index}`}
                className={`img-thumbnail ${img.isMain ? "border border-primary" : ""}`}
              />
              <button
                type="button"
                className="button button-danger"
                onClick={() => handleRemoveImageUrl(img.url, initialData?.id || 0)}
              >
                Remove
              </button>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="mainImage"
                  id={`mainImage${index}`}
                  checked={mainImageIndex === index}
                  onChange={() => {
                    setMainImageIndex(index);
                    
                    const updatedImageUrls = imageUrls.map((img: any, imgIndex: any) => ({
                      ...img,
                      isMain: imgIndex === index,
                    }));
                    setImageUrls(updatedImageUrls);
                  }}
                />
                <label className="form-check-label" htmlFor={`mainImage${index}`}>
                  Main Image
                </label>
              </div>
            </div>
          ))}
          {imageUrls.length < 5 && (
            <div {...getRootProps({ className: "dropzone" })} className="input-group">
              <input {...getInputProps()} />
              <p>Drag 'n' drop some files here, or click to select files</p>
            </div>
          )}
        </div>
        <div className="button-area mb-2">
          <button type="submit" className="btn btn-outline-success">
            {isEditing ? "Update" : "Add"} Package
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
};

export default PackageForm;
